import React, { useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  BarChart3,
  Rocket,
  LineChart,
  Command,
  FileText,
  BookOpen,
  Menu,
  X,
  Brain
} from 'lucide-react';
import { useSound } from '../hooks/useSound';
import { Logo } from './Logo';

const menuItems = [
  {
    name: 'Solutions',
    icon: Command,
    to: '/solutions',
    submenu: [
      { name: 'Strategic Planning', icon: BarChart3, to: '/solutions/strategic-planning' },
      { name: 'Operational Excellence', icon: Command, to: '/solutions/operational-excellence' },
      { name: 'Growth Acceleration', icon: Rocket, to: '/solutions/growth-acceleration' },
    ]
  },
  {
    name: 'Products',
    icon: LineChart,
    to: '/products',
    submenu: [
      { name: 'Command Center', icon: LineChart, to: '/products/command-center' },
      { name: 'Analytics Suite', icon: BarChart3, to: '/products/analytics' },
      { name: 'AI Insights', icon: Brain, to: '/products/ai-insights' },
    ]
  },
  { name: 'Case Studies', icon: FileText, to: '/case-studies' },
  { name: 'AI-Optimized Strategies', icon: Brain, to: '/pricing' },
  { name: 'Resources', icon: BookOpen, to: '/resources' }
];

const navVariants = {
  hidden: { y: -100, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 50,
      damping: 20,
      mass: 1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
      mass: 0.5
    }
  }
};

const mobileMenuVariants = {
  hidden: { 
    opacity: 0,
    height: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8
    }
  },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8,
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

export const Navigation = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playHover } = useSound();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    setIsOpen(false);
  }, [navigate]);

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <motion.nav
      variants={navVariants}
      initial="hidden"
      animate="visible"
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#000B1F]/95 backdrop-blur-xl shadow-lg shadow-amber-400/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <motion.div
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Logo className="text-amber-400" size="sm" />
              <span className="text-xl font-bold bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                ProfitIQ.AI
              </span>
            </motion.div>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.name}
                variants={itemVariants}
                custom={index}
                whileHover={{ 
                  scale: 1.05,
                  transition: { type: "spring", stiffness: 400, damping: 17 }
                }}
                className={`
                  text-gray-300 hover:text-amber-400 flex items-center space-x-1 cursor-pointer
                  ${location.pathname.startsWith(item.to) ? 'text-amber-400' : ''}
                `}
                onClick={() => handleNavigate(item.to)}
                onMouseEnter={playHover}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.name}</span>
              </motion.div>
            ))}

            <motion.button
              whileHover={{ 
                scale: 1.05,
                transition: { type: "spring", stiffness: 400, damping: 17 }
              }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-medium text-sm hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300"
              onClick={() => handleNavigate('/consultation')}
              onMouseEnter={playHover}
            >
              Get Started
            </motion.button>
          </div>

          <div className="md:hidden flex items-center">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-amber-400"
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="md:hidden bg-[#000B1F]/95 backdrop-blur-xl border-t border-amber-400/10"
          >
            <div className="px-4 pt-2 pb-3 space-y-1">
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  variants={itemVariants}
                  custom={index}
                  whileHover={{ x: 4 }}
                  className={`
                    text-gray-300 hover:text-amber-400 flex items-center space-x-2 px-3 py-2
                    ${location.pathname.startsWith(item.to) ? 'text-amber-400' : ''}
                  `}
                  onClick={() => handleNavigate(item.to)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </motion.div>
              ))}
              
              <motion.button
                whileHover={{ 
                  scale: 1.02,
                  transition: { type: "spring", stiffness: 400, damping: 17 }
                }}
                whileTap={{ scale: 0.98 }}
                className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-medium text-sm hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300"
                onClick={() => handleNavigate('/consultation')}
              >
                Get Started
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
});

Navigation.displayName = 'Navigation';